/**
 * Buffered SSE parser that handles:
 * - Multiple events in a single chunk
 * - Events split across chunk boundaries
 * - Malformed JSON data gracefully
 */

type ResponseMessageEvent = string;

interface SSEEvent {
  event: ResponseMessageEvent;
  data: { message: any };
}

export class SSEParser {
  private buffer = '';

  parseChunk(chunk: AllowSharedBufferSource): SSEEvent[] {
    const textDecoder = new TextDecoder('utf-8');
    this.buffer += textDecoder.decode(chunk, { stream: true });

    const events: SSEEvent[] = [];
    const parts = this.buffer.split('\n\n');
    this.buffer = parts.pop() || '';

    for (const part of parts) {
      const trimmed = part.trim();
      if (!trimmed) continue;

      const parsed = this.parseEvent(trimmed);
      if (parsed) {
        events.push(parsed);
      }
    }

    return events;
  }

  private parseEvent(eventBlock: string): SSEEvent | null {
    const fields: Record<string, string> = {};

    for (const line of eventBlock.split('\n')) {
      const colonIndex = line.indexOf(':');
      if (colonIndex === -1) continue;
      const field = line.substring(0, colonIndex).trim();
      const value = line.substring(colonIndex + 1).trim();
      if (field) {
        fields[field] = value;
      }
    }

    if (!fields.event) return null;

    let data: { message: any } = { message: '' };
    if (fields.data) {
      try {
        data = JSON.parse(fields.data);
      } catch (e) {
        console.error(`SSE: Failed to parse data for event "${fields.event}":`, fields.data, e);
        return null;
      }
    }

    return {
      event: fields.event,
      data,
    };
  }

  reset(): void {
    this.buffer = '';
  }
}
